/**
 * LIFF 経由でフォームを表示する公開ページ（Phase 24f）。
 * 仕様書: docs/01_spec/spec_line_bot.md A-12（LIFF 連携）
 *
 * 動作:
 *   1. Firestore settings/liff から LIFF ID を取得（公開読取ルール）
 *      取得できない場合は import.meta.env.VITE_LIFF_ID にフォールバック
 *   2. LIFF SDK を初期化
 *   3. LINE WebView 内（liff.isInClient() === true）なら liff.getProfile() で userId 取得
 *   4. URL クエリ ?formId=X&campaignId=Y から formId / campaignId を抽出
 *   5. 既存の PublicFormPage を再利用してフォーム表示（lineUid を prop で渡す）
 *   6. 通常ブラウザで開かれた場合（LIFF 初期化失敗）は lineUid なしでフォールバック表示
 *
 * LIFF ID 管理:
 *   admin-web の「設定 > 一般設定 > LIFF 設定」セクションから owner が更新可能。
 *   保存は updateLiffSettings Callable 経由（監査ログ liff_audit_logs に記録）。
 *
 * LINE Developers Console セットアップ:
 *   重要: LIFF は **LINE ログインチャネル** に追加する（Messaging API チャネル不可、2020/2 以降の仕様）
 *   1. 既存のプロバイダー配下に「LINE Login」チャネルを新規作成
 *   2. その LINE ログインチャネルの「LIFF」タブで LIFF アプリを追加
 *   3. エンドポイント URL: https://<hosting-domain>/liff/form
 *   4. Scope: profile（必須）
 *   5. 友だち追加オプション: On (Aggressive) + 同プロバイダー内の Messaging API チャネルを紐付け
 *      → LIFF 起動時に GoEnHub LINE Bot の友だち追加が促される
 *      → users/{lineUid}（LINE Bot 側）と mail_contacts.lineUid が一致
 */
import type { SettingsLiff } from '@goenhub/shared';
import liff from '@line/liff';
import { doc, getDoc } from 'firebase/firestore';
import { AlertCircle, Loader2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';


import { PublicFormPage } from './PublicFormPage';

import { db } from '@/lib/firebase';
import { usePublicPageMode } from '@/hooks/usePublicPageMode';


const LIFF_ID_FALLBACK = import.meta.env.VITE_LIFF_ID as string | undefined;

async function resolveLiffId(): Promise<string | null> {
  // 1. Firestore settings/liff（公開読取）を最優先
  try {
    const snap = await getDoc(doc(db, 'settings', 'liff'));
    if (snap.exists()) {
      const data = snap.data() as Partial<SettingsLiff>;
      if (typeof data.liffId === 'string' && data.liffId.trim()) {
        return data.liffId.trim();
      }
    }
  } catch (err) {
    // 公開ルールでも稀にネットワーク失敗等あり得るためフォールバックへ
    console.warn('[LiffFormPage] settings/liff fetch failed', err);
  }
  // 2. ビルド時環境変数フォールバック
  if (LIFF_ID_FALLBACK) {
    return LIFF_ID_FALLBACK;
  }
  return null;
}

type InitState =
  | { status: 'init' }
  | { status: 'ready'; lineUid: string | null }
  | { status: 'error'; message: string };

export function LiffFormPage() {
  usePublicPageMode();
  const [params] = useSearchParams();
  const formId = params.get('formId');
  const campaignId = params.get('campaignId');
  const [state, setState] = useState<InitState>({ status: 'init' });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const liffId = await resolveLiffId();
      if (cancelled) return;
      if (!liffId) {
        // LIFF ID 未設定（開発時等）→ lineUid なしでフォールバック
        setState({ status: 'ready', lineUid: null });
        return;
      }
      try {
        await liff.init({ liffId });
        if (cancelled) return;
        if (liff.isInClient()) {
          // LINE WebView 内：プロフィール取得
          try {
            const profile = await liff.getProfile();
            if (cancelled) return;
            setState({
              status: 'ready',
              lineUid: profile.userId ?? null,
            });
          } catch (err) {
            // プロフィール取得失敗 → lineUid なしで継続
            console.warn('[LiffFormPage] getProfile failed', err);
            setState({ status: 'ready', lineUid: null });
          }
        } else {
          // 外部ブラウザ：lineUid なしで継続
          setState({ status: 'ready', lineUid: null });
        }
      } catch (err) {
        // 初期化失敗（多くは正常なフォールバック）
        console.warn('[LiffFormPage] liff.init failed', err);
        if (cancelled) return;
        setState({ status: 'ready', lineUid: null });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (state.status === 'init') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="size-8 text-gray-400 animate-spin" />
      </div>
    );
  }

  if (!formId) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6">
        <div className="max-w-md w-full rounded-lg bg-white border border-gray-200 p-6 text-center space-y-3 shadow-sm">
          <AlertCircle className="size-10 mx-auto text-amber-500" />
          <h1 className="text-base font-bold text-gray-900">
            フォームが指定されていません
          </h1>
          <p className="text-xs text-gray-600 leading-relaxed">
            URL に <code>?formId=...</code> が必要です。
          </p>
        </div>
      </div>
    );
  }

  const lineUid = state.status === 'ready' ? state.lineUid : null;

  return (
    <PublicFormPage
      formIdOverride={formId}
      campaignIdOverride={campaignId}
      lineUid={lineUid ?? undefined}
    />
  );
}
